import { useCallback, useEffect, useState, type JSX, type UIEvent } from 'react';

import classNames from 'classnames';
import { Menu } from 'lucide-react';

import Breadcrumbs from '../Breadcrumbs/Breadcrumbs';
import AssistantWidget from '../AssistantWidget/AssistantWidget';
import { CommandPalette } from '../CommandPalette/CommandPalette';
import { CommandPaletteProvider } from '../CommandPalette/CommandPaletteContext';
import { CommandTrigger } from '../CommandPalette/CommandTrigger';
import {
  BillingStatusBanner,
  BlockedWorkspaceGate,
} from '../BillingStatusBanner/BillingStatusBanner';
import { ImpersonationBanner } from '../ImpersonationBanner';
import { SlackHealthBanner } from '../SlackHealthBanner/SlackHealthBanner';
import { WhatsNewButton } from '../WhatsNew/WhatsNewButton';
import { SupportBell } from '../../pages/Support/components/SupportBell';
import { useTenantFeatures } from '../../hooks/useTenantFeatures';
import { TenantFeature } from '../../types/tenant.types';
import { ThemeToggle } from '../ThemeToggle/ThemeToggle';

import EnhancedSidebar from './EnhancedSidebar';
import { PageHeaderSlotProvider, usePageHeaderNode } from './PageHeaderSlot';
import { SidebarUIProvider, useSidebarUI } from './SidebarUIContext';
import { TopNavCrumbProvider, useTopNavCrumbNode } from './TopNavCrumbSlot';

import { Outlet } from 'react-router-dom';
import { ProductUsageTracker } from '../ProductUsageTracker/ProductUsageTracker';
import styles from './Layout.module.css';

// Leaf consumer of the top-nav crumb slot. Isolating the useContext read here
// means changing the crumbs re-renders only this node, not the page subtree —
// so producers (PpShell) can push live crumbs without a render loop.
function TopNavCrumbs(): JSX.Element {
  const crumbNode = useTopNavCrumbNode();
  return <>{crumbNode ?? <Breadcrumbs />}</>;
}

function MobileMenuButton(): JSX.Element {
  const { openMobileSidebar } = useSidebarUI();
  return (
    <button
      type="button"
      className={styles.MobileMenuButton}
      onClick={openMobileSidebar}
      aria-label="Open navigation"
    >
      <Menu aria-hidden="true" />
    </button>
  );
}

/** A focused element that brings up the on-screen keyboard. */
function isEditable(element: Element | null): boolean {
  return !!element?.matches(
    'input, textarea, select, [contenteditable]:not([contenteditable="false"])',
  );
}

/**
 * Put the window back where the shell expects it once the keyboard is gone.
 *
 * Pages scroll inside `.Content`; the window never should. iOS Safari scrolls
 * the window anyway to lift a focused field above the keyboard, and does not
 * always scroll it back on blur, which leaves the shell shifted up with an
 * empty band below it (most visibly after typing in a field near the bottom
 * of a long page). Reset only when focus has left every editable field, so a
 * move from one field to the next keeps the lift the keyboard still needs.
 */
function useWindowStaysPut(): void {
  useEffect(() => {
    const onFocusOut = () => {
      requestAnimationFrame(() => {
        if (window.scrollY !== 0 && !isEditable(document.activeElement)) {
          window.scrollTo(0, 0);
        }
      });
    };
    document.addEventListener('focusout', onFocusOut);
    return () => document.removeEventListener('focusout', onFocusOut);
  }, []);
}

function LayoutContent(): JSX.Element {
  useWindowStaysPut();
  const { hasFeature } = useTenantFeatures();
  const hasSupport = hasFeature(TenantFeature.SUPPORT);
  const headerNode = usePageHeaderNode();
  // The top nav is translucent over page content; once the scroll container
  // moves it gains a solid background (the color transition lives in CSS) so it
  // stays legible over whatever scrolls beneath it.
  const [scrolled, setScrolled] = useState(false);
  const onContentScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const next = e.currentTarget.scrollTop > 4;
    setScrolled((prev) => (prev === next ? prev : next));
  }, []);
  return (
    <div className={styles.Layout}>
      <ProductUsageTracker />
      <ImpersonationBanner />
      {/* A Slack outage is silent by construction: the people who would notice
        are the ones not being messaged. This is the only thing that surfaces it. */}
      <BillingStatusBanner />
      <SlackHealthBanner />
      <EnhancedSidebar />
      <div className={styles.LayoutContent}>
        <div className={styles.Content} onScroll={onContentScroll}>
          <div className={classNames(styles.PageHeader, { [styles.PageHeaderScrolled]: scrolled })}>
            <MobileMenuButton />
            <div className={styles.PageHeaderInner}>
              {/* A page can take over the whole top nav via the PageHeaderSlot
                  (e.g. a focused create flow that supplies its own crumbs +
                  actions). Otherwise we render the standard top nav: route-matched
                  breadcrumbs on the left, persistent controls on the right. */}
              {headerNode ?? (
                <>
                  <div className={styles.TopNavLeft}>
                    {/* Route-matched breadcrumbs by default; a page (e.g. the
                        catalog/subscription surfaces) can replace them via the
                        TopNavCrumbSlot while keeping the controls on the right. */}
                    <TopNavCrumbs />
                  </div>
                  <div className={styles.TopNavRight}>
                    {/* Global ⌘K search, relocated here from the sidebar. */}
                    <CommandTrigger variant="topbar" />
                    <ThemeToggle />
                    {/* The platform's release log: the same for every workspace. */}
                    <WhatsNewButton />
                    {/* Notifications follow the person everywhere, not just /support:
                        everyone gets the notification center, Support adds its tickets. */}
                    <SupportBell support={hasSupport} />
                  </div>
                </>
              )}
            </div>
          </div>
          <BlockedWorkspaceGate>
            <Outlet />
          </BlockedWorkspaceGate>
        </div>
      </div>
      {/* Global ⌘K palette — mounted once at the Layout level. Its open state
          lives in CommandPaletteProvider so the sidebar trigger and the
          document-level shortcut share it. */}
      <CommandPalette />
      {/* Floating assistant, on every page except the assistant home
          (which embeds the full-page chat). */}
      <AssistantWidget />
    </div>
  );
}

export default function Layout(): JSX.Element {
  return (
    <CommandPaletteProvider>
      <SidebarUIProvider>
        <PageHeaderSlotProvider>
          <TopNavCrumbProvider>
            <LayoutContent />
          </TopNavCrumbProvider>
        </PageHeaderSlotProvider>
      </SidebarUIProvider>
    </CommandPaletteProvider>
  );
}
